import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Check, Clock, Repeat, ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import type { SearchResultItem } from '../../services/search';

interface SearchResultsListProps {
  operations: SearchResultItem[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
}

function formatAmount(value: number, currency = 'EUR', locale = 'fr-FR'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
}

function formatDate(value: string, locale = 'fr-FR'): string {
  const date = new Date(value);
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function SearchResultsList({
  operations,
  page,
  pageSize,
  total,
  onPageChange,
  isLoading,
}: SearchResultsListProps) {
  const { t, i18n } = useTranslation('search');
  const { translateCategoryName } = useCategoryTranslation();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';

  if (operations.length === 0 && !isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
        {t('results.empty')}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <th className="px-4 py-3 text-left font-semibold">{t('results.col.account')}</th>
              <th className="px-4 py-3 text-left font-semibold">{t('results.col.date')}</th>
              <th className="px-4 py-3 text-left font-semibold">{t('results.col.label')}</th>
              <th className="px-4 py-3 text-left font-semibold">{t('results.col.category')}</th>
              <th className="px-4 py-3 text-center font-semibold">{t('results.col.flags')}</th>
              <th className="px-4 py-3 text-right font-semibold">{t('results.col.amount')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {operations.map((op) => {
              const isCredit = op.operationType === 'credit';
              const categoryName = op.category
                ? translateCategoryName(op.category.id, op.category.name, op.category.isDefault)
                : t('results.uncategorized');
              const subcategoryName = op.subcategory
                ? translateCategoryName(op.subcategory.id, op.subcategory.name, op.subcategory.isDefault)
                : null;
              return (
                <tr key={`${op.source}-${op.id}`} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: op.bankAccount?.color || '#9ca3af' }}
                      />
                      <div className="min-w-0">
                        <div className="text-xs text-gray-400">
                          {op.source === 'shared' ? t('results.sourceShared') : t('results.sourcePersonal')}
                        </div>
                        <div className="text-sm text-gray-700 truncate">
                          {op.bankAccount?.name || t('results.noAccount')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {formatDate(op.operationDate, locale)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div className="flex items-center gap-2">
                      {op.isInternalTransfer && (
                        <ArrowLeftRight className="w-4 h-4 text-blue-500 shrink-0" />
                      )}
                      <span className="truncate">{op.customLabel || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <div className="truncate">{categoryName}</div>
                    {subcategoryName && (
                      <div className="text-xs text-gray-400 truncate">{subcategoryName}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      {op.isRecurring && (
                        <Repeat className="w-4 h-4 text-purple-500" aria-label={t('results.recurring')} />
                      )}
                      {op.isValidated ? (
                        <Check className="w-4 h-4 text-emerald-500" aria-label={t('results.validated')} />
                      ) : (
                        <Clock className="w-4 h-4 text-amber-500" aria-label={t('results.pending')} />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                    <span className={clsx(isCredit ? 'text-emerald-600' : 'text-rose-600')}>
                      {isCredit ? '+' : '−'} {formatAmount(op.amount, 'EUR', locale)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50 text-sm">
          <span className="text-gray-600">
            {t('results.pagination', {
              from: (page - 1) * pageSize + 1,
              to: Math.min(page * pageSize, total),
              total,
            })}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-gray-600">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
